import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Group,
  Image,
  Pagination,
  Progress,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconTrash, IconDownload, IconRefresh } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { BatchJobEntry, BatchJobStatus } from "@models/batch";
import { languageLabel } from "@config/languages";
import { usePagination } from "@hooks/usePagination";
import { useJobCost, formatUsd } from "@hooks/translation/useJobCost";
import emptyState from "@assets/icons/empty-state.svg";

const PAGE_SIZE = 10;

/** Per-row cost cell for a batch job; fetches its total cost (USD) lazily. */
const BatchCostCell: React.FC<{ jobId: string }> = ({ jobId }) => {
  const { t } = useTranslation();
  const { totalCostUsd, totalTokens } = useJobCost(jobId);
  if (totalCostUsd == null) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Tooltip label={t("translation.costTokens", { tokens: totalTokens.toLocaleString() })}>
      <Text size="xs" fw={600}>
        {formatUsd(totalCostUsd)}
      </Text>
    </Tooltip>
  );
};

interface Props {
  jobs: BatchJobEntry[];
  onRefresh: (jobId: string) => void;
  onDownload: (job: BatchJobEntry) => void;
  onRemove: (jobId: string) => void;
  onClear: () => void;
}

const statusColor = (status: BatchJobStatus | string): string => {
  switch (status) {
    case "completed":
      return "green";
    case "failed":
      return "red";
    case "queued":
    case "submitted":
      return "gray";
    case "pending":
      return "blue";
    default:
      return "gray";
  }
};

const statusLabel = (status: BatchJobStatus | string, t: (key: string) => string): string => {
  switch (status) {
    case "completed":
      return t("batch.status.completed");
    case "failed":
      return t("batch.status.failed");
    case "queued":
      return t("batch.status.queued");
    case "submitted":
      return t("batch.status.submitted");
    case "pending":
      return t("batch.status.pending");
    default:
      return status;
  }
};

export const BatchJobsTable: React.FC<Props> = ({
  jobs,
  onRefresh,
  onDownload,
  onRemove,
  onClear,
}) => {
  const { t } = useTranslation();
  const { page, setPage, pageItems, totalPages } = usePagination(jobs, PAGE_SIZE);

  if (jobs.length === 0) {
    return (
      <Center py={40}>
        <Stack align="center" gap="xs">
          <Image src={emptyState} w={100} opacity={0.5} alt="" />
          <Text c="dimmed" size="sm">{t("batch.noJobs")}</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="flex-end">
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          leftSection={<IconTrash size={14} />}
          onClick={onClear}
        >
          {t("batch.clearAll")}
        </Button>
      </Group>

      <Table.ScrollContainer minWidth={700}>
        <Table verticalSpacing="md" horizontalSpacing="lg" highlightOnHover layout="fixed" w="100%">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w="14%">{t("batch.table.file")}</Table.Th>
              <Table.Th w="20%">{t("batch.table.languages")}</Table.Th>
              <Table.Th w="10%">{t("batch.table.model")}</Table.Th>
              <Table.Th w="12%">{t("batch.table.progress")}</Table.Th>
              <Table.Th w="10%">{t("batch.table.status")}</Table.Th>
              <Table.Th w="10%">{t("translation.cost")}</Table.Th>
              <Table.Th w="13%">{t("batch.table.submitted")}</Table.Th>
              <Table.Th w="11%" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pageItems.map((job) => {
              const progress =
                job.totalUnits > 0
                  ? Math.round((job.completedUnits / job.totalUnits) * 100)
                  : 0;
              const isTerminal = job.status === "completed" || job.status === "failed";
              // A completed job whose merged file isn't ready yet is still
              // being finalised: keep showing progress/refresh affordances and
              // a "preparing" hint instead of a (missing) download button.
              const isPreparingDownload =
                job.status === "completed" && !job.resultsUrl;
              const showRefresh = !isTerminal || isPreparingDownload;

              return (
                <Table.Tr key={job.jobId}>
                  <Table.Td maw={180}>
                    <Text size="sm" lineClamp={1} title={job.fileName}>
                      {job.fileName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {job.totalUnits} {t("batch.units")}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="wrap">
                      <Badge size="xs" variant="light" color="brand" style={{ maxWidth: "none" }}>
                        {languageLabel(job.sourceLang)}
                      </Badge>
                      <Text size="xs" c="dimmed">→</Text>
                      <Badge size="xs" variant="light" color="accent" style={{ maxWidth: "none" }}>
                        {languageLabel(job.targetLang)}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="outline" color="gray">
                      {job.model}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Progress
                      value={progress}
                      size="xs"
                      color={job.status === "failed" ? "red" : "brand"}
                      animated={!isTerminal || isPreparingDownload}
                    />
                    <Text size="xs" c="dimmed" ta="center" mt={2}>
                      {job.completedUnits}/{job.totalUnits}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={statusColor(job.status)}
                      variant="light"
                      style={{ maxWidth: "none" }}
                    >
                      {statusLabel(job.status, t)}
                    </Badge>
                    {isPreparingDownload && (
                      <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
                        {t("batch.preparingDownload")}
                      </Text>
                    )}
                    {job.error && (
                      <Text size="xs" c="red" lineClamp={1} mt={2}>
                        {job.error}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <BatchCostCell jobId={job.jobId} />
                  </Table.Td>
                  <Table.Td style={{ whiteSpace: "nowrap" }}>
                    <Text size="xs" c="dimmed">
                      {dayjs(job.createdAt).format("DD MMM, HH:mm")}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      {showRefresh && (
                        <Tooltip label={t("batch.refresh")}>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => onRefresh(job.jobId)}
                            aria-label={t("batch.refresh")}
                          >
                            <IconRefresh size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {job.status === "completed" && job.resultsUrl && (
                        <Tooltip label={t("batch.download")}>
                          <ActionIcon
                            variant="subtle"
                            color="brand"
                            size="sm"
                            onClick={() => onDownload(job)}
                            aria-label={t("batch.download")}
                          >
                            <IconDownload size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label={t("common.delete")}>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() => onRemove(job.jobId)}
                          aria-label={t("common.delete")}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {totalPages > 1 && (
        <Group justify="center" mt="xs">
          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            size="sm"
            radius="md"
            color="brand"
          />
        </Group>
      )}
    </Stack>
  );
};
